import { z } from 'zod';
import {
  BillSchema,
  LienWaiverSchema,
  isBillEditable,
  type Bill,
  type LienWaiver,
} from '../../domain/entities/bill.ts';
import { CostLineSchema, type CostLine } from '../../domain/entities/cost-line.ts';
import { newId } from '../../domain/id.ts';
import { expectUnchanged } from '../../ports/batch.ts';
import type { ClockPort } from '../../ports/clock-port.ts';
import type { DataPort } from '../../ports/data-port.ts';
import type { FunctionsPort } from '../../ports/functions-port.ts';
import type { Unsubscribe } from '../../ports/query.ts';
import { NotFoundError } from '../../ports/repository.ts';

/**
 * Writing bills, and nothing that changes their state or their money.
 *
 * Raising one, moving it, and recording a payment are callables, for three
 * separate reasons: the **number** is sequential per job; **ready for
 * payment** is a decision somebody is accountable for and has a permission
 * flag of its own; and a **payment** is a record of money having left the
 * bank, which a client must not be able to invent.
 *
 * What is here is what the person entering the invoice does.
 */
export class BillError extends Error {
  readonly reason: 'invalid' | 'notEditable';

  constructor(reason: 'invalid' | 'notEditable', message: string) {
    super(message);
    this.name = 'BillError';
    this.reason = reason;
  }
}

export const SaveBillInputSchema = z.object({
  title: z.string().trim().min(1, 'A bill needs a title').max(200),
  payToContactId: z.string().min(1).optional(),
  description: z.string().max(20_000).optional(),
  invoiceDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .optional(),
  dueDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .optional(),
  purchaseOrderId: z.string().min(1).nullable().optional(),
  lines: z.array(CostLineSchema).optional(),
  billableToOwner: z.boolean().optional(),
  /** What the supplier charged in tax, and whether we bought exempt. */
  taxRateId: z.string().min(1).optional(),
  taxRateMillionths: z.number().int().min(0).max(1_000_000).optional(),
  taxExempt: z.boolean().optional(),
  tagIds: z.array(z.string().min(1)).optional(),
});
export type SaveBillInput = z.input<typeof SaveBillInputSchema>;

export interface BillContext {
  readonly data: DataPort;
  readonly clock: ClockPort;
  readonly companyId: string;
  readonly jobId: string;
  readonly uid: string;
}

type ReadContext = Pick<BillContext, 'data' | 'companyId' | 'jobId'>;

function repo(context: ReadContext) {
  return context.data.collection<Bill>(
    { companyId: context.companyId, jobId: context.jobId },
    'bills',
  );
}

/** A job's bills, highest number first — the padding makes that numeric. */
export async function listBills(context: ReadContext): Promise<Bill[]> {
  const page = await repo(context).list({ orderBy: [{ field: 'number', direction: 'desc' }] });
  return [...page.items];
}

export function subscribeToBills(
  context: ReadContext,
  onChange: (bills: readonly Bill[]) => void,
  onError: (cause: Error) => void,
): Unsubscribe {
  return repo(context).subscribe(
    { orderBy: [{ field: 'number', direction: 'desc' }] },
    (page) => onChange(page.items),
    onError,
  );
}

export async function saveBill(
  context: BillContext,
  id: string,
  input: SaveBillInput,
): Promise<Bill> {
  const parsed = SaveBillInputSchema.safeParse(input);
  if (!parsed.success) throw new BillError('invalid', parsed.error.issues[0]!.message);

  const existing = await repo(context).get(id);
  if (!existing) throw new NotFoundError('bills', id);
  if (!isBillEditable(existing.status)) {
    throw new BillError(
      'notEditable',
      existing.status === 'void'
        ? 'A voided bill cannot be changed.'
        : 'This bill is marked ready for payment. Put it back to open to change it.',
    );
  }

  /** `null` clears, `undefined` leaves alone — as on a purchase order. */
  const optional = <K extends keyof Bill>(key: K, value: string | null | undefined) =>
    value === null ? { [key]: undefined } : value !== undefined ? { [key]: value } : {};

  const patch = {
    title: parsed.data.title,
    description: parsed.data.description ?? existing.description,
    lines: parsed.data.lines ?? existing.lines,
    billableToOwner: parsed.data.billableToOwner ?? existing.billableToOwner,
    /*
      Frozen on the bill like an invoice's rate, so a bill from last
      year goes on saying what was charged at the time even after the
      rate record is edited or retired.
    */
    taxRateMillionths: parsed.data.taxRateMillionths ?? existing.taxRateMillionths,
    taxExempt: parsed.data.taxExempt ?? existing.taxExempt,
    ...optional('taxRateId', parsed.data.taxRateId),
    tagIds: parsed.data.tagIds ?? existing.tagIds,
    ...optional('payToContactId', parsed.data.payToContactId),
    ...optional('invoiceDate', parsed.data.invoiceDate),
    ...optional('dueDate', parsed.data.dueDate),
    ...optional('purchaseOrderId', parsed.data.purchaseOrderId),
    updatedAt: context.clock.now().toISOString(),
    updatedBy: context.uid,
  };

  const checked = BillSchema.safeParse({ ...existing, ...patch });
  if (!checked.success) throw new BillError('invalid', checked.error.issues[0]!.message);

  await context.data.commit([
    {
      kind: 'update',
      scope: { companyId: context.companyId, jobId: context.jobId },
      collection: 'bills' as const,
      id,
      patch,
      expect: expectUnchanged(existing),
    },
  ]);
  return checked.data;
}

/**
 * Record or clear a bill's lien waiver (P5-10c).
 *
 * **Its own function, and allowed on a bill that is ready for payment** —
 * which is the whole reason it is not part of `saveBill`. The order of
 * events is: the office approves the bill for payment, and *then* chases
 * the sub for the waiver. A rule that only let an open bill change would
 * mean putting an approved bill back to open to file a document, which is
 * a worse record of what happened.
 *
 * The security rule allows the same narrow thing: a write that touches
 * `lienWaiver` and the audit fields and nothing else, whatever the status.
 * `firebase/rules.test.ts` asserts both halves — the narrow write allowed
 * on a ready bill, a wider one refused.
 *
 * A voided bill is refused: there is nothing left to waive.
 */
export async function saveLienWaiver(
  context: BillContext,
  id: string,
  waiver: LienWaiver | null,
): Promise<Bill> {
  const existing = await repo(context).get(id);
  if (!existing) throw new NotFoundError('bills', id);
  if (existing.status === 'void') {
    throw new BillError('notEditable', 'A voided bill has nothing left to waive.');
  }

  let next: LienWaiver | undefined;
  if (waiver !== null) {
    const parsed = LienWaiverSchema.safeParse(waiver);
    if (!parsed.success) throw new BillError('invalid', parsed.error.issues[0]!.message);
    next = parsed.data;
  }

  const patch = {
    lienWaiver: next,
    updatedAt: context.clock.now().toISOString(),
    updatedBy: context.uid,
  };

  const checked = BillSchema.safeParse({ ...existing, ...patch });
  if (!checked.success) throw new BillError('invalid', checked.error.issues[0]!.message);

  await context.data.commit([
    {
      kind: 'update',
      scope: { companyId: context.companyId, jobId: context.jobId },
      collection: 'bills' as const,
      id,
      patch,
      expect: expectUnchanged(existing),
    },
  ]);
  return checked.data;
}

/** Replace one line, keeping the rest. Pure; the caller then saves. */
export function withBillLineReplaced(bill: Pick<Bill, 'lines'>, line: CostLine): CostLine[] {
  const existing = bill.lines.findIndex((candidate) => candidate.id === line.id);
  if (existing === -1) return [...bill.lines, line];
  return bill.lines.map((candidate) => (candidate.id === line.id ? line : candidate));
}

export function withBillLineRemoved(bill: Pick<Bill, 'lines'>, lineId: string): CostLine[] {
  return bill.lines.filter((line) => line.id !== lineId);
}

// ---- the callables, wrapped ---------------------------------------------

/**
 * Raise a bill and get its number.
 *
 * The id is the caller's unless supplied, which is what makes a retry
 * safe: a dropped response and a second attempt converge on one bill with
 * one number rather than two documents a bookkeeper has to reconcile.
 */
export async function createBill(
  functions: FunctionsPort,
  input: {
    readonly companyId: string;
    readonly jobId: string;
    readonly title: string;
    readonly payToContactId?: string;
    readonly purchaseOrderId?: string;
    readonly billId?: string;
  },
): Promise<{ readonly billId: string; readonly number: string }> {
  const billId = input.billId ?? newId();
  return functions.call('createBill', {
    companyId: input.companyId,
    jobId: input.jobId,
    billId,
    title: input.title,
    ...(input.payToContactId === undefined ? {} : { payToContactId: input.payToContactId }),
    ...(input.purchaseOrderId === undefined ? {} : { purchaseOrderId: input.purchaseOrderId }),
  });
}

export async function transitionBill(
  functions: FunctionsPort,
  input: {
    readonly companyId: string;
    readonly jobId: string;
    readonly billId: string;
    readonly to: 'open' | 'readyForPayment' | 'void';
  },
): Promise<{ readonly status: string }> {
  return functions.call('transitionBill', input);
}

/**
 * Record a payment that has already happened outside this system.
 *
 * The id is generated here unless supplied, so a double-clicked `Record
 * payment` is one payment rather than two — which on this callable is the
 * difference between a correct ledger and a job that looks overpaid.
 */
export async function recordBillPayment(
  functions: FunctionsPort,
  input: {
    readonly companyId: string;
    readonly jobId: string;
    readonly billId: string;
    readonly amountMinor: number;
    readonly method: 'check' | 'card' | 'ach' | 'cash' | 'other';
    readonly reference?: string;
    readonly paymentId?: string;
  },
): Promise<{ readonly paymentId: string; readonly paidMinor: number }> {
  const paymentId = input.paymentId ?? newId();
  return functions.call('recordBillPayment', {
    companyId: input.companyId,
    jobId: input.jobId,
    billId: input.billId,
    paymentId,
    amountMinor: input.amountMinor,
    method: input.method,
    ...(input.reference === undefined ? {} : { reference: input.reference }),
  });
}
